import { React, useContext, useEffect, useState } from "react";
import { PageContext } from "../../App";
import { createAPIRequest } from "../WebUtils";
import { REQUEST_URL, STATUS_CHECK_INTERVAL } from "../Config";
import {
  base64ToBlob,
  convertImageToMimeType,
  fileToBase64,
  formatBytes,
  getFileExtension,
  saveFile,
} from "../Utils";

function UpscaleUploadView(props) {
  // AppStatus.ts
  const AppState = {
    PROCESSING: "PROCESSING",
    PROCESSED: "PROCESSED",
    NONE: "NONE",
  };

  //get context
  const {
    selectedFiles,
    setSelectedFiles,
    setShowEditFileScreen,
    setStartProcessingFiles,
  } = useContext(PageContext);

  //on submit button clicked
  const [submitClicked, setSubmitClicked] = useState(false);
  //file upload details
  const [uploadDetails, setUploadDetails] = useState({
    state: AppState.NONE,
    processedImageBase64String: null, //image received from server after processing
    message: "",
  });

  //output settings
  const [outputSettings, setOutputSettings] = useState({
    scale: { scale: 2, popupShowing: false },
    enhanceFace: true,
    type: {
      popupShowing: false,
      export_as: ".jpg",
      mimetype: "image/jpeg",
    },
  });

  //start
  useEffect(() => {
    //check
    if (submitClicked === true) {
      //upload files
      if (selectedFiles.length > 0) {
        //set
        const selectedFile = selectedFiles[0];
        //set message
        setUploadDetails({
          ...uploadDetails,
          state: AppState.PROCESSING,
        });
        //upload file
        processImage(selectedFile, REQUEST_URL);
      }
    }

    //check
    if (selectedFiles.length === 0) {
      //reset
      resetScreen();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFiles, submitClicked]);

  //reset screen
  //remove files info
  //empty selected files list
  const resetScreen = () => {
    //set flags
    setSelectedFiles([]);
    setUploadDetails({
      state: AppState.NONE,
      processedImageBase64String: null, //image received from server after processing
      message: "",
    });
    setShowEditFileScreen(false);
    setStartProcessingFiles(false);
  };

  //check status from status from endpoint
  const checkRunpodJobStatus = async (url, job_id) => {
    //check runpod status
    //access token append automatically
    const response = await createAPIRequest(url, "POST", "application/json", {
      request_type: "STATUS_CHECK_UPSCALE_IMAGE",
      job_id: job_id,
    });

    //check success
    if (response.success === true) {
      //check status
      if (response.status === "COMPLETED") {
        //result json
        const result = response.message.image;
        //check
        if (result !== null) {
          //set
          setUploadDetails({
            ...uploadDetails,
            state: AppState.PROCESSED,
            message: "",
            processedImageBase64String: result,
          });
          //set
          setSubmitClicked(false);
        }

        console.log("Response: ", result);
      } else if (
        response.status === "FAILED" ||
        response.status === "CANCELLED" ||
        response.status === "TIMED_OUT"
      ) {
        //set message
        setUploadDetails({
          ...uploadDetails,
          state: AppState.NONE,
          message:
            response.message === null
              ? "Sorry, something went wrong. Please try again with different image."
              : response.message,
        });

        //set
        setSubmitClicked(false);
      } else if (
        response.status === "IN_QUEUE" ||
        response.status === "RUNNING" ||
        response.status === "IN_PROGRESS"
      ) {
        //check api after 10 seconds
        setTimeout(() => {
          //callback
          checkRunpodJobStatus(url, job_id);
        }, STATUS_CHECK_INTERVAL);
      }
    } else {
      //set message
      setUploadDetails({
        ...uploadDetails,
        state: AppState.NONE,
        message:
          response.message === null
            ? "Sorry, something went wrong. Please try again with different image."
            : response.message,
      });

      //set
      setSubmitClicked(false);
    }
  };

  //send file to server as base64
  //process image
  //and get output
  const processImage = async (file, url) => {
    //check file size
    if (file.size < props.maxFileSize) {
      //check file type
      if (props.allowedFileExtensions.includes(getFileExtension(file.name))) {
        //base 64 data
        const imgData = await fileToBase64(file);
        //slit
        const dataArr = imgData.split(",");
        //check
        if (dataArr.length > 1) {
          //base64Data
          const base64Data = dataArr[1];
          //apin request esrgan
          //access token append automatically
          const response = await createAPIRequest(
            url,
            "POST",
            "application/json",
            {
              request_type: "UPSCALE_IMAGE",
              img_data: base64Data,
              scale_amount: Number(outputSettings.scale.scale),
              enhance_face: outputSettings.enhanceFace,
              job_id: null,
            },
          );

          //check
          //if failed
          if (response.success === false) {
            //set message
            setUploadDetails({
              ...uploadDetails,
              state: AppState.NONE,
              message: response.message,
            });
            //set
            setSubmitClicked(false);
          } else {
            //set token in local storage
            localStorage.setItem("access_token", response.token);
            //set job_id
            const job_id = response.job_id;
            //check response from runpod server
            //when humanize text is completed
            if (
              response.status === "FAILED" ||
              response.status === "CANCELLED" ||
              response.status === "TIMED_OUT" ||
              job_id === null
            ) {
              //set message
              setUploadDetails({
                ...uploadDetails,
                state: AppState.NONE,
                message:
                  response.message === null
                    ? "Sorry, something went wrong. Please try again with different image."
                    : response.message,
              });

              //set
              setSubmitClicked(false);
            } else {
              //check status for job_id
              checkRunpodJobStatus(url, job_id);
            }
          }
        } else {
          //set message
          setUploadDetails({
            ...uploadDetails,
            state: AppState.NONE,
            message: "Invalid image file.",
          });
          //set
          setSubmitClicked(false);
        }
      }
    } else {
      //set message
      setUploadDetails({
        ...uploadDetails,
        state: AppState.NONE,
        message: `Sorry, we can’t process this file because it exceeds the maximum size limit. Please upload a smaller file. The maximum allowed size is - ${formatBytes(props.maxFileSize)}`,
      });
      //set
      setSubmitClicked(false);
    }
  };

  return (
    <div
      className="position-relative container bg-light p-5 rounded-2"
      style={{ marginTop: "6rem" }}
    >
      {/* Reset button */}
      <button
        className="position-absolute btn btn-transparent"
        style={{ top: "10px", right: "10px" }}
        onClick={resetScreen}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="26"
          height="26"
          fill="#000000"
          className="bi bi-arrow-clockwise"
          viewBox="0 0 16 16"
        >
          <path
            fillRule="evenodd"
            d="M8 3a5 5 0 1 0 4.546 2.914.5.5 0 0 1 .908-.417A6 6 0 1 1 8 2z"
          />
          <path d="M8 4.466V.534a.25.25 0 0 1 .41-.192l2.36 1.966c.12.1.12.284 0 .384L8.41 4.658A.25.25 0 0 1 8 4.466" />
        </svg>
      </button>

      {/* original Image */}
      <div className="position-relative text-center">
        {/* title */}
        <div className="text-center">
          <label className="fs-3 text-success lead">Original Image</label>
        </div>

        {/* Image */}
        <img
          src={
            selectedFiles.length > 0
              ? URL.createObjectURL(selectedFiles[0])
              : "./images/file_icon.png"
          }
          className="mt-5"
          alt="upscale_image"
          style={{
            width: "200px",
            height: "auto",
            opacity:
              selectedFiles.length > 0
                ? selectedFiles[0] === null
                  ? 0.2
                  : 1
                : 0,
          }}
        />

        {/* Name */}
        <div
          className={`d-flex mt-4 align-items-center justify-content-center ${uploadDetails.state === AppState.NONE ? "d-none" : ""}`}
        >
          {/* Label */}
          <label className="fs-4 text-secondary">
            {selectedFiles.length > 0 ? selectedFiles[0].name : "unknown"}
          </label>

          {/* Progress */}
          <div
            className={`spinner-border text-info ms-3 ${uploadDetails.state === AppState.PROCESSING ? "" : "d-none"}`}
            role="status"
          ></div>
        </div>

        {/* Output Setting */}
        <div className="d-flex text-center justify-content-center align-items-center mt-3">
          {/* Enhance Face */}
          <div className="d-flex me-2">
            {/* label */}
            <label className="text-primary fs-4">Enhance Face:</label>
            {/* Checkbox Enhance Face */}
            <input
              className="form-check-input shadow my-auto ms-2"
              type="checkbox"
              value=""
              checked={outputSettings.enhanceFace}
              id="enhanceFace"
              onChange={(e) => {
                //set
                setOutputSettings({
                  ...outputSettings,
                  enhanceFace: e.target.checked,
                });
              }}
            />
          </div>

          {/* Scale */}
          <div className="d-flex ms-2">
            {/* label */}
            <label className="text-primary fs-4">Scale:</label>
            {/* Dropdown menu */}
            <div className="dropdown ms-2">
              <label
                className="dropdown-toggle fs-4 text-secondary"
                type="text"
                id="dropdownMenuScale"
                data-toggle="dropdown"
                aria-haspopup="true"
                aria-expanded="false"
                style={{ cursor: "pointer" }}
                onClick={(e) => {
                  //show hide color picker
                  //turn other popups off
                  setOutputSettings({
                    ...outputSettings,
                    scale: {
                      ...outputSettings.scale,
                      popupShowing: outputSettings.scale.popupShowing
                        ? false
                        : true,
                    },
                    type: {
                      ...outputSettings.type,
                      popupShowing: false,
                    },
                  });
                }}
              >
                {String(outputSettings.scale.scale)}X
              </label>
              <div
                className={`dropdown-menu ${
                  outputSettings.scale.popupShowing === true ? "show" : ""
                }`}
                aria-labelledby="dropdownMenuScale"
              >
                {[2, 3, 4].map((item, index) => (
                  <button
                    key={index}
                    className="dropdown-item"
                    type="button"
                    onClick={(e) => {
                      //show hide color picker
                      //turn other popups off
                      setOutputSettings({
                        ...outputSettings,
                        type: {
                          ...outputSettings.type,
                          popupShowing: false,
                        },
                        scale: {
                          ...outputSettings.scale,
                          scale: item,
                          popupShowing: false,
                        },
                      });
                    }}
                  >
                    {String(item)}X
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Export As */}
        <div className="d-flex text-center justify-content-center align-items-center mt-2">
          {/* label */}
          <label className="text-primary fs-4">Save As:</label>
          {/* Dropdown menu */}
          <div className="dropdown ms-2 my-auto">
            <label
              className="dropdown-toggle fs-4 text-secondary"
              type="text"
              id="dropdownMenuImageType"
              data-toggle="dropdown"
              aria-haspopup="true"
              aria-expanded="false"
              style={{ cursor: "pointer" }}
              onClick={(e) => {
                //toggle
                setOutputSettings({
                  ...outputSettings,
                  type: {
                    ...outputSettings.type,
                    popupShowing:
                      outputSettings.type.popupShowing === true ? false : true,
                  },
                  scale: {
                    ...outputSettings.scale,
                    popupShowing: false,
                  },
                });
              }}
            >
              {outputSettings.type.export_as}
            </label>
            <div
              className={`dropdown-menu ${
                outputSettings.type.popupShowing === true ? "show" : ""
              }`}
              aria-labelledby="dropdownMenuImageType"
            >
              <button
                className="dropdown-item"
                type="button"
                onClick={(e) => {
                  //set configuration
                  setOutputSettings({
                    ...outputSettings,
                    type: {
                      ...outputSettings.type,
                      popupShowing: false,
                      export_as: ".jpg",
                      mimetype: "image/jpg",
                    },
                    scale: {
                      ...outputSettings.scale,
                      popupShowing: false,
                    },
                  });
                }}
              >
                .jpg
              </button>
              <button
                className="dropdown-item"
                type="button"
                onClick={(e) => {
                  //set configuration
                  setOutputSettings({
                    ...outputSettings,
                    type: {
                      ...outputSettings.type,
                      popupShowing: false,
                      export_as: ".jpeg",
                      mimetype: "image/jpeg",
                    },
                    scale: {
                      ...outputSettings.scale,
                      popupShowing: false,
                    },
                  });
                }}
              >
                .jpeg
              </button>
              <button
                className="dropdown-item"
                type="button"
                onClick={(e) => {
                  //set configuration
                  setOutputSettings({
                    ...outputSettings,
                    type: {
                      ...outputSettings.type,
                      popupShowing: false,
                      export_as: ".png",
                      mimetype: "image/png",
                    },
                    scale: {
                      ...outputSettings.scale,
                      popupShowing: false,
                    },
                  });
                }}
              >
                .png
              </button>
              <button
                className="dropdown-item"
                type="button"
                onClick={(e) => {
                  //set configuration
                  setOutputSettings({
                    ...outputSettings,
                    type: {
                      ...outputSettings.type,
                      popupShowing: false,
                      export_as: ".bmp",
                      mimetype: "image/bmp",
                    },
                    scale: {
                      ...outputSettings.scale,
                      popupShowing: false,
                    },
                  });
                }}
              >
                .bmp
              </button>
              <button
                className="dropdown-item"
                type="button"
                onClick={(e) => {
                  //set configuration
                  setOutputSettings({
                    ...outputSettings,
                    type: {
                      ...outputSettings.type,
                      popupShowing: false,
                      export_as: ".webp",
                      mimetype: "image/webp",
                    },
                    scale: {
                      ...outputSettings.scale,
                      popupShowing: false,
                    },
                  });
                }}
              >
                .webp
              </button>
              <button
                className="dropdown-item"
                type="button"
                onClick={(e) => {
                  //set configuration
                  setOutputSettings({
                    ...outputSettings,
                    type: {
                      ...outputSettings.type,
                      popupShowing: false,
                      export_as: ".svg",
                      mimetype: "image/svg+xml",
                    },
                    scale: {
                      ...outputSettings.scale,
                      popupShowing: false,
                    },
                  });
                }}
              >
                .svg
              </button>
            </div>
          </div>
        </div>

        {/* Upscale button */}
        <button
          className="btn btn-primary mt-5 fs-3 px-4"
          disabled={submitClicked}
          onClick={(e) => {
            //set
            setSubmitClicked(true);
          }}
        >
          Upscale
        </button>

        {/* Message */}
        <div className={`mt-5 ${uploadDetails.message === "" ? "d-none" : ""}`}>
          <label className="fs-4 text-danger">{uploadDetails.message}</label>
        </div>
      </div>

      {/* Upscaled Image */}
      <div
        className={`border-top pt-3 mt-5 ${uploadDetails.state === AppState.PROCESSED && uploadDetails.processedImageBase64String !== null ? "" : "d-none"}`}
      >
        {/* title */}
        <div className="text-center">
          <label className="fs-3 text-success lead">Upscaled Image</label>
        </div>

        {/* Image */}
        <div className="text-center mt-5 mb-4">
          <img
            src={`data:${selectedFiles.length > 0 ? selectedFiles[0].type : "image/jpeg"};base64,${uploadDetails.processedImageBase64String}`}
            alt="upscale_image"
            style={{
              width: "80%",
              height: "auto",
              maxHeight: "80%",
            }}
          />
        </div>

        {/* Download button */}
        <div className="text-center mt-5">
          <button
            className="btn btn-success px-4 py-2 fs-3"
            onClick={async (e) => {
              //upscale image
              const upscaledImage = await base64ToBlob(
                uploadDetails.processedImageBase64String,
                selectedFiles.length > 0 ? selectedFiles[0].type : "image/jpeg",
              );
              //converted blob
              const imgBlob = await convertImageToMimeType(
                new File(
                  [upscaledImage],
                  selectedFiles.length > 0 ? selectedFiles[0].name : "upscaled",
                ),
                outputSettings.type.mimetype,
                1.0,
              );
              //save blob
              saveFile(
                imgBlob,
                selectedFiles.length > 0 ? selectedFiles[0].name : "upscaled",
              );
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              fill="currentColor"
              className="bi bi-download me-2"
              viewBox="0 0 16 16"
            >
              <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5" />
              <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708z" />
            </svg>
            Download
          </button>
        </div>
      </div>
    </div>
  );
}

export default UpscaleUploadView;
